/**
 * Broadcast windows are Eastern, for everyone.
 *
 * Same call as the portfolio digest (`DIGEST_TIMEZONE`) and the Foghorn
 * schedule (`FOGHORN_TIME_ZONE`): the company works Eastern, and a broadcast is
 * one company-wide surface, so there is no per-user timezone to carry.
 *
 * NONE OF THIS CHANGES WHAT IS STORED. `starts_at` and `ends_at` are
 * timestamptz, absolute instants, and Postgres compares them against now()
 * with no timezone in play. This module governs only what a human types into
 * the composer and reads back in the list. Before it, `<input
 * type="datetime-local">` was interpreted as the AUTHOR's browser timezone and
 * the list was rendered in the READER's, so a broadcast written on a laptop set
 * to Central expired an hour off what everyone else assumed, silently.
 */

export const BROADCAST_TIME_ZONE = 'America/New_York';

/** Offset of the zone at a given instant, in ms (positive = ahead of UTC). */
export function zoneOffsetMs(instant: number): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: BROADCAST_TIME_ZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(new Date(instant));
  const get = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? '0');
  const asIfUtc = Date.UTC(
    get('year'),
    get('month') - 1,
    get('day'),
    get('hour'),
    get('minute'),
    get('second'),
  );
  return asIfUtc - instant;
}

/**
 * 'YYYY-MM-DDTHH:mm' read as EASTERN wall time -> the instant it names, as an
 * ISO string. Returns null if the input is not a datetime-local value.
 *
 * Two passes, because the offset depends on the instant and the instant
 * depends on the offset. One refinement settles every case except the hour DST
 * skips each March, which is not a nameable wall time under any scheme.
 */
export function easternWallToIso(wall: string): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(wall.trim());
  if (!m) return null;
  const [, year, month, day, hour, minute, second] = m;
  if (!year || !month || !day || !hour || !minute) return null;
  const naive = Date.UTC(+year, +month - 1, +day, +hour, +minute, second ? +second : 0);
  let instant = naive - zoneOffsetMs(naive);
  instant = naive - zoneOffsetMs(instant);
  return new Date(instant).toISOString();
}

/** The Eastern wall clock at an instant, in the shape datetime-local wants. */
export function isoToEasternWall(instant: number): string {
  return new Date(instant + zoneOffsetMs(instant)).toISOString().slice(0, 16);
}

/** Default window: a week out, 5pm Eastern. */
export function defaultEnd(now: number = Date.now()): string {
  const sevenDaysOut = now + 7 * 24 * 60 * 60 * 1000;
  return `${isoToEasternWall(sevenDaysOut).slice(0, 10)}T17:00`;
}

/**
 * Render a stored instant for the admin list. The zone is stated, not implied:
 * a bare "5:00 PM" on a company-wide list is a question, not an answer.
 */
export function formatWhen(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.toLocaleString('en-US', {
    timeZone: BROADCAST_TIME_ZONE,
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })} ET`;
}
